import React, { useRef, useState } from "react";
import { Download, Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Trash2, Plus, Pencil } from "lucide-react";
import { Button } from "../ui/button";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

const save = (text, name) => {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

const ICONS = {
  new: <Plus size={13} className="text-teal" />,
  updated: <Pencil size={13} className="text-navy" />,
  delete: <Trash2 size={13} className="text-red-500" />,
  problem: <AlertTriangle size={13} className="text-orange-dark" />,
};

const WORDS = { new: "New answer", updated: "Updates an answer", delete: "Deletes an answer", problem: "Can't import this row" };

export const AskCassieImport = ({ onImported }) => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState("");
  const [allowPublish, setAllowPublish] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mig, setMig] = useState(null);
  const [migBusy, setMigBusy] = useState(false);

  const download = async (which) => {
    try {
      const text =
        which === "template"
          ? await adminApi.askCassie.templateCsv()
          : which === "export"
            ? await adminApi.askCassie.exportCsv()
            : await adminApi.askCassie.faqMigrationCsv(which === "audit" ? "audit" : "import");
      const names = {
        template: "ask-cassie-template.csv",
        export: `ask-cassie-answers-${new Date().toISOString().slice(0, 10)}.csv`,
        audit: "faq-migration-audit.csv",
        migration: "ask-cassie-faq-migration-import.csv",
      };
      save(text, names[which]);
    } catch {
      toast({ title: "Couldn't download that file", variant: "destructive" });
    }
  };

  const loadMigration = async () => {
    setMigBusy(true);
    try {
      setMig(await adminApi.askCassie.faqMigration());
    } catch {
      toast({ title: "Couldn't read the existing FAQs", variant: "destructive" });
    } finally {
      setMigBusy(false);
    }
  };

  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(null);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(file, "utf-8");
  };

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const res = await adminApi.askCassie.importCsv({ csv, dry_run: dryRun, allow_publish: allowPublish });
      setPreview(res);
      if (res.applied) {
        toast({ title: "Imported", description: `${res.summary.new} new, ${res.summary.updated} updated, ${res.summary.deleted} deleted.` });
        onImported?.();
      }
    } catch (e) {
      toast({ title: "Couldn't read that file", description: e?.response?.data?.detail || "Please check the format.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const s = preview?.summary;
  const blocked = !!s && s.problems > 0;

  return (
    <div data-testid="ask-cassie-import">
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => download("export")} className="bg-navy hover:bg-navy/90 text-white rounded-full" data-testid="ask-cassie-export">
          <Download size={15} className="mr-1.5" /> Download every answer
        </Button>
        <Button onClick={() => download("template")} variant="outline" className="rounded-full" data-testid="ask-cassie-template">
          <FileSpreadsheet size={15} className="mr-1.5" /> Download the blank template
        </Button>
      </div>

      <div className="mt-6 rounded-2xl border-2 border-dashed border-slate-200 p-6">
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={pick} className="hidden" data-testid="ask-cassie-file" />
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => fileRef.current?.click()} variant="outline" className="rounded-full" data-testid="ask-cassie-choose-file">
            <Upload size={15} className="mr-1.5" /> Choose a CSV file
          </Button>
          <span className="text-sm text-slate-500">{fileName || "No file chosen"}</span>
        </div>

        <label className="flex items-start gap-2.5 mt-5 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={allowPublish}
            onChange={(e) => { setAllowPublish(e.target.checked); setPreview(null); }}
            className="mt-0.5 w-4 h-4 accent-orange"
            data-testid="ask-cassie-allow-publish"
          />
          <span>
            Let this file publish answers
            <span className="block text-xs text-slate-500">
              Off by default: everything new or changed lands as a draft for you to approve. Tick this only when the
              <code className="mx-1 text-[11px] bg-slate-100 px-1 rounded">status</code> column really is your approval.
            </span>
          </span>
        </label>

        <div className="flex flex-wrap gap-3 mt-5">
          <Button onClick={() => run(true)} disabled={!csv || busy} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="ask-cassie-preview">
            {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Check the file
          </Button>
          {preview && !preview.applied && !blocked && (
            <Button onClick={() => run(false)} disabled={busy} className="bg-teal hover:bg-teal/90 text-white rounded-full" data-testid="ask-cassie-apply">
              Import {s.new + s.updated + s.deleted} row{s.new + s.updated + s.deleted === 1 ? "" : "s"}
            </Button>
          )}
        </div>
      </div>

      {preview && (
        <div className="mt-6" data-testid="ask-cassie-import-result">
          <div className={`rounded-xl p-4 flex items-start gap-2.5 ${preview.applied ? "bg-teal/5 border border-teal/30" : blocked ? "bg-orange/5 border border-orange/30" : "bg-slate-50 border border-slate-200"}`}>
            {preview.applied ? <CheckCircle2 size={16} className="text-teal shrink-0 mt-0.5" /> : <AlertTriangle size={16} className={`shrink-0 mt-0.5 ${blocked ? "text-orange-dark" : "text-slate-400"}`} />}
            <div>
              <p className="text-sm text-navy font-semibold">
                {preview.applied
                  ? "Imported"
                  : blocked
                    ? "Nothing has been written yet — some rows need fixing first"
                    : "Nothing has been written yet — this is what would happen"}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                {s.new} new · {s.updated} updated · {s.deleted} deleted · {s.problems} with problems
                {blocked && " — fix those rows and check the file again. It's all or nothing, so nothing is half-imported."}
              </p>
              {blocked && (
                <p className="text-xs text-slate-500 mt-1.5">
                  Moving the library to the live site rather than editing it? Use "Moving the library to the live site"
                  above — it carries the approvals and the settings as they are, without a tickbox.
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-3">Row</th><th className="py-2 pr-3">Question</th><th className="py-2 pr-3">What happens</th>
              </tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.line} className="border-b border-slate-50 align-top" data-testid={`import-row-${r.line}`}>
                    <td className="py-2 pr-3 text-slate-400 text-xs">{r.line}</td>
                    <td className="py-2 pr-3 text-navy max-w-[340px]">{r.question || r.slug}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                        {ICONS[r.action]} {WORDS[r.action]}
                      </span>
                      {r.problems.length > 0 && (
                        <ul className="mt-1 text-xs text-orange-dark list-disc pl-4 space-y-0.5">
                          {r.problems.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8 rounded-2xl bg-slate-50 p-6" data-testid="ask-cassie-migration">
        <h4 className="font-display font-bold text-navy text-sm">Bringing the existing FAQs across (Phase 3 groundwork)</h4>
        <p className="text-xs text-slate-500 mt-1.5 leading-relaxed max-w-3xl">
          Reads the questions already on the site — the FAQ page, the chatbot and the service pages — and prepares two
          spreadsheets: an audit to work through, and an import file of the same questions as drafts. Nothing is
          written to the library and nothing on the site changes until you import. Questions about the firm are
          suggested for <strong>Working with CASS</strong>; anything that matched nothing at all is parked in
          "Needs a subject" for you to place, rather than filed on a guess.
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <Button onClick={loadMigration} disabled={migBusy} variant="outline" className="rounded-full" data-testid="ask-cassie-migration-check">
            {migBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} What would this involve?
          </Button>
          {mig && (
            <>
              <Button onClick={() => download("audit")} variant="outline" className="rounded-full" data-testid="ask-cassie-migration-audit">
                <Download size={15} className="mr-1.5" /> The audit
              </Button>
              <Button onClick={() => download("migration")} variant="outline" className="rounded-full" data-testid="ask-cassie-migration-import">
                <Download size={15} className="mr-1.5" /> The import file
              </Button>
            </>
          )}
        </div>
        {mig && (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm" data-testid="ask-cassie-migration-stats">
            {[
              [`${mig.total} questions on the site today`, `${mig.from_faqs_page} on the FAQ page and in the chatbot · ${mig.from_service_pages} on service pages`],
              [`${mig.distinct_questions} distinct questions`, `${mig.possible_duplicates} look like duplicates — the audit says which to check, and nothing is dropped automatically`],
              [`${mig.ready_to_import} have a category suggested`, "from their old category, the service page they sit on, or as a Working with CASS answer"],
              [`${mig.working_with_cass} suggested for Working with CASS`, "about the firm rather than a subject: cost, switching, onboarding, how we work"],
              [`${mig.needs_a_subject_decision} parked for a decision`, "matched no subject and aren't about the firm — they land in \"Needs a subject\" for you to place"],
              [`${mig.suggested_indexed} suggested for Google`, `${mig.suggested_noindex} suggested noindex — reachable and linked, but kept out of search so the library stays subject-led`],
              [`${mig.answers_too_long} answers run long`, "trim to two to four sentences, or promote to a full page"],
              [`${mig.contradiction_candidates} mention e-commerce or Shopify`, "the brief flagged a contradiction here — check it reads consistently"],
            ].map(([head, sub]) => (
              <div key={head} className="rounded-xl bg-white border border-slate-200 p-4">
                <p className="font-display font-bold text-navy text-sm">{head}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{sub}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <details className="mt-6 text-sm">
        <summary className="cursor-pointer font-semibold text-navy">What each column means</summary>
        <div className="mt-3 text-xs text-slate-600 leading-relaxed space-y-1.5">
          <p><strong>slug</strong> — the web address. Leave blank on a new row and it's built from the question. Matching is by this: same address updates that answer, a new one creates it.</p>
          <p><strong>question, quick_answer</strong> — both required. The quick answer is two to four sentences.</p>
          <p><strong>category</strong> — one of: bookkeeping-xero, tax-vat-mtd, payroll-pensions, management-accounts, outsourced-finance, solicitors-accounts, working-with-cass. Or <code>needs-a-subject</code> to park a row in the holding list instead of guessing — parked rows are never published or indexed.</p>
          <p><strong>level</strong> — <code>quick</code> (no page of its own) or <code>detailed</code> (its own page). Sections are ignored on a quick answer.</p>
          <p><strong>section_1…5_heading / _body</strong> — the detail, in order. Leave a blank line inside a body for a new paragraph.</p>
          <p><strong>related_slugs</strong> — other answers' addresses, separated by <code>|</code>. Chosen by hand, never generated.</p>
          <p><strong>service_label / service_url, cta_label / cta_url</strong> — one service link and one call to action.</p>
          <p><strong>success_story</strong> — the address of a success story, or blank.</p>
          <p><strong>review_frequency</strong> — six_months, annual or two_years. <strong>reviewed_at</strong> — YYYY-MM-DD.</p>
          <p><strong>featured</strong> — yes/no, for "Most asked" on the hub.</p>
          <p><strong>indexable</strong> — yes/no, or blank to use the default for the category. Subject answers default to yes; a working-with-cass answer defaults to yes only for cost, switching, onboarding and getting-started questions, and no otherwise.</p>
          <p><strong>old_category, appears_on, migration_notes</strong> — carried through from the migration audit so the holding list can show where a question came from and why it's waiting. Ignored otherwise.</p>
          <p><strong>status</strong> — draft or published. A change to this column is only acted on when you tick the publish box above; otherwise an answer keeps the status it already has, so re-importing an export can never unpublish something.</p>
          <p><strong>delete</strong> — set to <code>yes</code> to remove that answer. Everything else in the row is ignored.</p>
          <p>Nothing outside the file is touched: answers you don't include are left exactly as they are.</p>
        </div>
      </details>
    </div>
  );
};

export default AskCassieImport;
